import React from "react";
import { MILESTONES } from "../../../../data/constants";
import SelectWrapper from "../../shared/components/SelectWrapper";
import Toggle from "../../shared/components/Toggle";
import DocChips from "../../shared/components/DocChips";
import Modal from "../../shared/components/Modal";
import styles from "../../../../styles/AdminConsole.module.css";

/**
 * EditVisitModal
 *
 * Props:
 *   visit             object  — the visit being edited (used for title: visit.orderId)
 *   form              { milestone, date, description, concern, cleared, paidINR, documents }
 *   setForm           (updater) => void
 *   docInput          string
 *   setDocInput       (val) => void
 *   uploading         bool
 *   saving            bool
 *   onFileUpload      (e: ChangeEvent) => void
 *   onSave            () => void
 *   onClose           () => void
 */
export default function EditVisitModal({
  visit,
  form,
  setForm,
  docInput,
  setDocInput,
  uploading,
  saving,
  onFileUpload,
  onSave,
  onClose,
}) {
  const handleAddDoc = () => {
    const v = docInput.trim();
    if (!v || form.documents.includes(v)) return;
    setForm((f) => ({ ...f, documents: [...f.documents, v] }));
    setDocInput("");
  };

  const handleRemoveDoc = (item) =>
    setForm((f) => ({
      ...f,
      documents: f.documents.filter((x) => x !== item),
    }));

  return (
    <Modal
      title={`Edit Visit — ${visit.orderId}`}
      onClose={onClose}
      maxWidth={440}
    >
      <div className={styles.formInner}>
        <div>
          <span
            className={styles.toggleLabelText}
            style={{ display: "block", marginBottom: 4, fontSize: 10 }}
          >
            Milestone *
          </span>
          <SelectWrapper
            value={form.milestone}
            onChange={(e) =>
              setForm((f) => ({ ...f, milestone: e.target.value }))
            }
          >
            {MILESTONES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </SelectWrapper>
        </div>

        <div>
          <span
            className={styles.toggleLabelText}
            style={{ display: "block", marginBottom: 4, fontSize: 10 }}
          >
            Visit Date *
          </span>
          <input
            className={styles.input}
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
        </div>

        <textarea
          className={styles.input}
          placeholder="Description"
          rows={3}
          style={{ resize: "vertical", lineHeight: 1.5 }}
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
        />

        <textarea
          className={styles.input}
          placeholder="Concern (leave blank if none)"
          rows={2}
          style={{ resize: "vertical", lineHeight: 1.5 }}
          value={form.concern}
          onChange={(e) => setForm((f) => ({ ...f, concern: e.target.value }))}
        />

        <div>
          <span
            className={styles.toggleLabelText}
            style={{ display: "block", marginBottom: 4, fontSize: 10 }}
          >
            Paid for this Visit (₹)
          </span>
          <input
            className={`${styles.input} ${styles.inputNumber}`}
            type="number"
            min="0"
            step="1"
            placeholder="0"
            value={form.paidINR}
            onChange={(e) =>
              setForm((f) => ({ ...f, paidINR: e.target.value }))
            }
          />
        </div>

        <Toggle
          label="Cleared"
          checked={!!form.cleared}
          onChange={() => setForm((f) => ({ ...f, cleared: !f.cleared }))}
        />

        <div>
          <span
            className={styles.toggleLabelText}
            style={{ display: "block", marginBottom: 6, fontSize: 10 }}
          >
            Documents
          </span>
          <DocChips
            items={form.documents}
            inputValue={docInput}
            onInputChange={setDocInput}
            onAdd={handleAddDoc}
            onRemove={handleRemoveDoc}
            onFileUpload={onFileUpload}
            uploading={uploading}
          />
        </div>

        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={saving || uploading}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
}
